import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db749pgd6mcdj80oo7jg";

type Status = "idle" | "loading" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("留言已提交，感谢你的反馈！");
      form.reset();
      return;
    }

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setFeedback("请填写完整的称呼、邮箱与留言内容。");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setStatus("error");
      setFeedback("请输入有效的邮箱地址。");
      return;
    }

    formData.delete("website_alt");
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus("loading");
    setFeedback("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setFeedback("留言已提交，感谢你的反馈！");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(
          typeof serverMessage === "string" && serverMessage
            ? serverMessage
            : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交失败，请稍后重试。");
    } finally {
      setStatus((prev) => (prev === "loading" ? "idle" : prev));
    }
  };

  const inputClass =
    "w-full h-11 px-3.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400";

  return (
    <form
      id="site-feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="flex flex-col gap-2">
          <span className="text-sm text-foreground-700">你的称呼</span>
          <input name="name" type="text" placeholder="请输入称呼" className={inputClass} required />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-sm text-foreground-700">联系邮箱</span>
          <input name="email" type="email" placeholder="name@example.com" className={inputClass} required />
        </label>
      </div>

      <label className="flex flex-col gap-2 mt-4">
        <span className="text-sm text-foreground-700">留言主题</span>
        <select name="subject" className={inputClass} defaultValue="作品资料纠错">
          <option value="作品资料纠错">作品资料纠错</option>
          <option value="内容建议">内容建议</option>
          <option value="合作与投稿">合作与投稿</option>
          <option value="其他问题">其他问题</option>
        </select>
      </label>

      <label className="flex flex-col gap-2 mt-4">
        <span className="text-sm text-foreground-700">留言内容</span>
        <textarea
          name="message"
          rows={5}
          maxLength={500}
          placeholder="请简要描述你的问题或建议（最多 500 字）"
          className="w-full px-3.5 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 resize-none"
          required
        ></textarea>
      </label>

      <input
        type="text"
        name="website_alt"
        className="legacy-website-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {feedback && (
        <p
          className={[
            "mt-4 text-sm flex items-center gap-2",
            status === "success" ? "text-secondary-700" : "text-accent-800",
          ].join(" ")}
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i
              className={status === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"}
            ></i>
          </span>
          {feedback}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        className="mt-6 inline-flex items-center gap-2 px-6 h-11 rounded-md bg-accent-500 text-accent-950 text-sm font-medium hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <span className="w-4 h-4 flex items-center justify-center">
          <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
        </span>
        {status === "loading" ? "提交中..." : "提交留言"}
      </button>
    </form>
  );
}